import type { AssignableProjectMember } from '@/modules/projects/assignable-members.api'
import { getAssignableProjectMembers } from '@/modules/projects/assignable-members.api'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { queryKeys } from './query-keys'
import { useWorkspaceSession } from './useWorkspaceSession'

const EMPTY_MEMBERS: AssignableProjectMember[] = []

interface UseProjectMembersOptions {
  enabled?: boolean
}

/** One query per project and immutable access session; no separate global cache. */
export function useProjectMembers(
  projectId: string | undefined,
  options: UseProjectMembersOptions = {},
) {
  const scope = useWorkspaceSession()
  const client = useQueryClient()
  const prefix = projectId
    ? [...queryKeys.projectMembers.list(projectId), 'assignable-users']
    : [...queryKeys.members.all, 'display', 'assignable-users']
  const queryKey = [...prefix, { workspaceSession: scope.key }]
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      scope.run(signal, linked =>
        getAssignableProjectMembers(projectId, linked),
      ),
    enabled: options.enabled !== false && scope.enabled,
    staleTime: (projectId ? 10 : 5) * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
  return {
    members:
      scope.enabled && options.enabled !== false && !query.isError
        ? (query.data ?? EMPTY_MEMBERS)
        : EMPTY_MEMBERS,
    isLoading: query.isLoading,
    error: scope.enabled && options.enabled !== false ? query.error : null,
    invalidate: () => client.invalidateQueries({ queryKey }),
  }
}
